/*
 * Copyright (C) 2016 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useScope as createI18nScope} from '@canvas/i18n'
import classnames from 'classnames'
import {Link} from '@instructure/ui-link'
import {Text} from '@instructure/ui-text'
import {ProgressBar} from '@instructure/ui-progress'
import {Heading} from '@instructure/ui-heading'
import {Tooltip} from '@instructure/ui-tooltip'

const I18n = createI18nScope('student_context_traySubmissionProgressBars')

// @ts-expect-error TS7006 (typescriptify)
function scoreInPoints(score, pointsPossible) {
  const formattedScore = I18n.n(score, {precision: 2, strip_insignificant_zeros: true})
  const formattedPointsPossible = I18n.n(pointsPossible, {
    precision: 2,
    strip_insignificant_zeros: true,
  })
  return `${formattedScore}/${formattedPointsPossible}`
}

class SubmissionProgressBars extends React.Component {
  static propTypes = {
    submissions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        score: PropTypes.number,
        user: PropTypes.shape({
          _id: PropTypes.string.isRequired,
        }).isRequired,
        assignment: PropTypes.shape({
          html_url: PropTypes.string.isRequired,
          points_possible: PropTypes.number,
        }),
      }).isRequired,
    ).isRequired,
  }

  // @ts-expect-error TS7006 (typescriptify)
  static displayGrade(submission) {
    const {score, grade, excused} = submission
    const pointsPossible = submission.assignment.points_possible
    let display

    if (excused) {
      display = 'EX'
    } else if (grade.match(/%/)) {
      // Grade is a percentage, just show it
      display = grade
    } else if (grade.match(/complete/)) {
      // Grade is complete/incomplete, show icon
      display = SubmissionProgressBars.renderIcon(grade)
    } else {
      // Default to show score out of points possible
      display = scoreInPoints(score, pointsPossible)
    }

    return display
  }

  // @ts-expect-error TS7006 (typescriptify)
  static displayScreenreaderGrade(submission) {
    const {score, grade, excused} = submission
    const pointsPossible = submission.assignment.points_possible
    let display

    if (excused) {
      display = I18n.t('excused')
    } else if (grade.match(/%/) || grade.match(/complete/)) {
      // Grade is a percentage or in/complete, just show it
      display = grade
    } else {
      // Default to show score out of points possible
      display = scoreInPoints(score, pointsPossible)
    }

    return display
  }

  // @ts-expect-error TS7006 (typescriptify)
  static renderIcon(grade) {
    const iconClass = classnames({
      'icon-check': grade === 'complete',
      'icon-x': grade === 'incomplete',
    })

    return (
      <div>
        <span className="screenreader-only">{I18n.t('%{grade}', {grade})}</span>
        <i className={iconClass} data-testid={`submission-grade-icon-${grade}`} />
      </div>
    )
  }

  render() {
    // @ts-expect-error TS2339,TS7006 (typescriptify)
    const submissions = this.props.submissions.filter(s => s.grade != null)
    if (submissions.length > 0) {
      return (
        <section
          className="StudentContextTray__Section StudentContextTray-Progress"
          data-testid="submission-progress-section"
        >
          <Heading level="h4" as="h3" border="bottom" data-testid="submission-progress-header">
            {I18n.t('Last %{length} Graded Items', {length: submissions.length})}
          </Heading>
          {/* @ts-expect-error TS7006 (typescriptify) */}
          {submissions.map(submission => (
            <div
              key={submission.id}
              className="StudentContextTray-Progress__Bar"
              data-testid="submission-progress-bar"
            >
              <Tooltip renderTip={submission.assignment.name} placement="top">
                <Link
                  href={`${submission.assignment.html_url}/submissions/${submission.user._id}`}
                  // @ts-expect-error TS2769 (typescriptify)
                  themeOverride={{textDecoration: 'none'}}
                  display="block"
                  data-testid="submission-link"
                >
                  <ProgressBar
                    size="small"
                    // @ts-expect-error TS2769 (typescriptify)
                    successColor={false}
                    label={I18n.t('Grade')}
                    valueMax={submission.assignment.points_possible}
                    valueNow={submission.score || 0}
                    screenReaderLabel={SubmissionProgressBars.displayScreenreaderGrade(submission)}
                    renderValue={() => (
                      <Text size="x-small" color="secondary" data-testid="submission-grade-text">
                        {SubmissionProgressBars.displayGrade(submission)}
                      </Text>
                    )}
                  />
                </Link>
              </Tooltip>
            </div>
          ))}
        </section>
      )
    } else {
      return null
    }
  }
}

export default SubmissionProgressBars
